import {
  WebRTCManager,
  WebRTCConfig,
  ICECandidateHandler,
  ConnectionStateHandler,
} from "@/utils/webrtc";
import { CallRoomModel } from "@/models/InboundCall/CallRoomModel";
import {
  CallSessionModel,
  CallSessionStatus,
} from "@/models/InboundCall/CallSessionModel";
import { InboundCallServiceClient } from "@/serviceClients/InboundCallServiceClient";
import {
  isKokattoErrorResponse,
  KokattoErrorResponse,
} from "@/serviceClients/responses/KokattoErrorResponse";
import store from "@/store";
import { RefreshableRequestHelper } from "@/helpers/RefreshableRequestHelper";
import {
  OutboundCallAnswerRequest,
  OutboundCallInitiateRequest,
  OutboundCallEndRequest,
  OutboundCallICECandidateRequest,
} from "@/serviceClients/requests/OutboundCallRequest";
import {
  OutboundCallAnswerResponse,
  OutboundCallInitiateResponse,
} from "@/serviceClients/responses/OutboundCallResponse";
import { HoldCallRoomRequest } from "@/serviceClients/requests/InboundCallRequest";
import { HoldCallRoomResponse } from "@/serviceClients/responses/InboundCallResponse";
import { ERROR_REASON } from "@/store/modules/ActiveCallController/constants";
import { InboundCallRejectIncomingRequest } from "@/serviceClients/requests/InboundCallRejectIncomingRequest";
import { ProcessingIncomingCallResponse } from "@/serviceClients/responses/KokattoCallCenterResponse";

export enum OutboundCallState {
  IDLE = "idle",
  REQUESTING_PERMISSION = "requesting_permission",
  PERMISSION_GRANTED = "permission_granted",
  PERMISSION_DENIED = "permission_denied",
  INITIALIZING = "initializing",
  CALLING = "calling",
  RINGING = "ringing",
  CONNECTED = "connected",
  ENDING = "ending",
  ENDED = "ended",
  FAILED = "failed",
}

export interface OutboundCallEvents {
  onStateChange?: (state: OutboundCallState) => void;
  onError?: (error: Error) => void;
  onConnected?: () => void;
  onEnded?: (reason?: string) => void;
  onRemoteStreamAvailable?: (stream: MediaStream) => void;
}

export class OutboundCallController {
  private webrtc: WebRTCManager;
  private room: CallRoomModel;
  private session: CallSessionModel;
  private inboundCallServiceClient: InboundCallServiceClient;
  private currentState: OutboundCallState = OutboundCallState.IDLE;
  private events: OutboundCallEvents = {};
  private callId: string | null = null;
  private startTime: number | null = null;
  private endTime: number | null = null;
  private remoteAudioElement: HTMLAudioElement | null = null;
  private isOnHold = false;

  constructor(
    room: CallRoomModel,
    session: CallSessionModel,
    events?: OutboundCallEvents
  ) {
    const config: WebRTCConfig = {
      iceServers: [
        {
          urls: [
            "turn:98.85.84.164:3478?transport=udp",
            "turn:98.85.84.164:3478?transport=tcp",
          ],
          username: "kokatto-turn-user",
          credential: "k0katt0s3cr3t",
        },
      ],
      iceCandidatePoolSize: 10,
    };

    this.webrtc = new WebRTCManager(config);
    this.room = room;
    this.session = session;
    this.inboundCallServiceClient = new InboundCallServiceClient({
      token: store.getters.getKokattoTokenAccess,
    });
    this.events = events || {};

    this.setupWebRTCHandlers();
  }

  private setupWebRTCHandlers(): void {
    // Handle ICE candidates
    const iceCandidateHandler: ICECandidateHandler = () => {
      // this.handleICECandidate(candidate);
    };
    this.webrtc.setICECandidateHandler(iceCandidateHandler);

    // Handle connection state changes
    const connectionStateHandler: ConnectionStateHandler = (state) => {
      this.handleConnectionStateChange(state);
    };
    this.webrtc.setConnectionStateHandler(connectionStateHandler);

    const peerConnection = this.webrtc.getPeerConnection();

    peerConnection.ontrack = (event) => {
      if (event.streams && event.streams.length > 0) {
        const remoteStream = event.streams[0];

        // Add tracks to WebRTC manager's remote stream (mimic WhatsApp behavior)
        const webrtcRemoteStream = this.webrtc.getRemoteStream();
        if (webrtcRemoteStream) {
          event.streams[0].getTracks().forEach((track) => {
            webrtcRemoteStream.addTrack(track);
          });
        }

        // Create audio element immediately
        this.setupRemoteAudio(remoteStream);

        // Call the callback for any additional handling
        if (this.events.onRemoteStreamAvailable) {
          this.events.onRemoteStreamAvailable(remoteStream);
        }
      }
    };

    // Add connection state logging
    peerConnection.onconnectionstatechange = () => {
      console.log("🔗 PeerConnection state:", peerConnection.connectionState);
    };

    peerConnection.oniceconnectionstatechange = () => {
      console.log(
        "🧊 ICE connection state:",
        peerConnection.iceConnectionState
      );
    };
  }

  private setupRemoteAudio(remoteStream: MediaStream): void {
    // Clean up existing audio element
    if (this.remoteAudioElement) {
      this.remoteAudioElement.pause();
      this.remoteAudioElement.srcObject = null;
      this.remoteAudioElement.remove();
    }

    // Create new audio element (exactly like WhatsApp implementation)
    this.remoteAudioElement = document.createElement("audio");
    this.remoteAudioElement.srcObject = remoteStream;
    this.remoteAudioElement.autoplay = true;
    this.remoteAudioElement.controls = false;
    this.remoteAudioElement.style.display = "none";

    // Append to document body (like WhatsApp does)
    document.body.appendChild(this.remoteAudioElement);

    // Force play with comprehensive error handling
    this.remoteAudioElement
      .play()
      .then(() => {
        console.log("🔊 Audio play() succeeded");
      })
      .catch((error) => {
        console.error("🔊 Audio play() failed:", error);

        if (error.name === "NotAllowedError") {
          console.log(
            "🔊 Autoplay blocked - trying user interaction workaround"
          );
          // Create a user interaction prompt
          this.showPlayAudioPrompt();
        } else if (error.name === "AbortError") {
          console.log("🔊 Play aborted - retrying");
          setTimeout(() => {
            this.remoteAudioElement?.play();
          }, 1000);
        }
      });
  }

  private showPlayAudioPrompt(): void {
    // Simple prompt to get user interaction for autoplay
    const playButton = document.createElement("button");
    playButton.textContent = "Enable Call Audio";
    playButton.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 10000;
      padding: 15px 30px;
      background: #4CAF50;
      color: white;
      border: none;
      border-radius: 5px;
      font-size: 16px;
      cursor: pointer;
    `;

    playButton.onclick = () => {
      if (this.remoteAudioElement) {
        this.remoteAudioElement
          .play()
          .then(() => {
            console.log("🔊 Manual play succeeded");
            document.body.removeChild(playButton);
          })
          .catch((err) => {
            console.error("🔊 Manual play failed:", err);
          });
      }
    };

    document.body.appendChild(playButton);
  }

  private handleConnectionStateChange(state: RTCPeerConnectionState): void {
    switch (state) {
      case "connecting":
        this.setState(OutboundCallState.CALLING);
        break;
      case "connected":
        this.setState(OutboundCallState.CONNECTED);
        this.startTime = Date.now();
        if (this.events.onConnected) {
          this.events.onConnected();
        }
        break;
      case "disconnected":
        this.setState(OutboundCallState.ENDED);
        this.endTime = Date.now();
        if (this.events.onEnded) {
          this.events.onEnded("disconnected");
        }
        break;
      case "failed":
        this.setState(OutboundCallState.FAILED);
        if (this.events.onError) {
          this.events.onError(new Error("WebRTC connection failed"));
        }
        break;
    }
  }

  // Add debugging method
  async getConnectionDebugInfo(): Promise<any> {
    const pc = this.webrtc.getPeerConnection();
    const remoteStream = this.webrtc.getRemoteStream();

    return {
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
      signalingState: pc.signalingState,
      remoteStreamTracks:
        remoteStream?.getTracks().map((t) => ({
          kind: t.kind,
          enabled: t.enabled,
          readyState: t.readyState,
          id: t.id,
        })) || [],
      localStreamTracks:
        this.webrtc
          .getLocalStream()
          ?.getTracks()
          .map((t) => ({
            kind: t.kind,
            enabled: t.enabled,
            readyState: t.readyState,
            id: t.id,
          })) || [],
      audioElementExists: !!this.remoteAudioElement,
      audioElementSrc: this.remoteAudioElement?.srcObject ? "set" : "null",
      audioElementReadyState: this.remoteAudioElement?.readyState,
      audioElementPaused: this.remoteAudioElement?.paused,
    };
  }

  private setState(newState: OutboundCallState): void {
    if (this.currentState !== newState) {
      this.currentState = newState;
      if (this.events.onStateChange) {
        this.events.onStateChange(newState);
      }
    }
  }

  async checkMicrophonePermission(): Promise<boolean> {
    try {
      const permission = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });

      if (permission.state !== "granted") {
        if (permission.state === "denied") {
          throw new Error(ERROR_REASON.MIC_DENIED);
        } else {
          throw new Error(ERROR_REASON.MIC_NOT_GRANTED);
        }
      }
      return true;
    } catch (error) {
      console.warn("Could not check microphone permission:", error);

      if ((error as Error).message.includes(ERROR_REASON.MIC_DENIED)) {
        store.dispatch("ActiveCallController/setSnackbarState", {
          color: "#FFF2E0",
          isActive: true,
          message: "$vuetify.inboundCallMicPermissionErrorMessage",
          timeout: -1,
          reason: ERROR_REASON.MIC_DENIED,
        });
      } else if (
        (error as Error).message.includes(ERROR_REASON.MIC_NOT_GRANTED)
      ) {
        store.dispatch("ActiveCallController/setSnackbarState", {
          color: "#FFF2E0",
          isActive: true,
          message: "$vuetify.inboundCallMicPermissionErrorMessage",
          timeout: -1,
          reason: ERROR_REASON.MIC_NOT_GRANTED,
        });
      }

      // Fallback: try to get media stream
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        stream.getTracks().forEach((track) => track.stop());
        return true;
      } catch {
        return false;
      }
    }
  }

  async getAvailableDevices(): Promise<{
    audioInputs: MediaDeviceInfo[];
    audioOutputs: MediaDeviceInfo[];
  }> {
    const [audioInputs, audioOutputs] = await Promise.all([
      this.webrtc.getAudioDevices(),
      this.webrtc.getAudioOutputDevices(),
    ]);

    return { audioInputs, audioOutputs };
  }

  async initiateCall(): Promise<void> {
    try {
      this.setState(OutboundCallState.INITIALIZING);

      // Check microphone permission
      const hasPermission = await this.checkMicrophonePermission();
      if (!hasPermission) {
        throw new Error("Microphone permission is required to make calls");
      }

      // Initialize media stream
      await this.webrtc.initializeLocalStream();

      // Prepare initiate request
      const initiateRequest: OutboundCallInitiateRequest = {
        roomId: this.session.roomId,
      };

      // Call backend to initiate call
      const initiateResponse:
        | OutboundCallInitiateResponse
        | KokattoErrorResponse = await RefreshableRequestHelper.requestKokatto<
        OutboundCallInitiateResponse | KokattoErrorResponse
      >(() =>
        this.inboundCallServiceClient.initiateOutboundCall(initiateRequest)
      );

      if (isKokattoErrorResponse(initiateResponse)) {
        console.log(
          "initiateResponse isKokattoErrorResponse",
          JSON.stringify(initiateResponse, null, 2)
        );

        throw new Error(
          `Failed to initiate call: ${
            initiateResponse.message || initiateResponse.reason
          }`
        );
      }

      this.callId = initiateResponse.chat.roomId;
      this.setState(OutboundCallState.CALLING);

      // Set remote description (offer from backend)
      if (initiateResponse.session.sdp) {
        await this.webrtc.setRemoteDescription({
          type: "offer",
          sdp: initiateResponse.session.sdp,
        });

        // Create and set answer
        const answer = await this.createCompleteAnswer();
        // const answer = await this.webrtc.createAnswer();

        // Send answer to backend
        const answerRequest: OutboundCallAnswerRequest = {
          roomId: initiateResponse.chat.roomId,
          sessionExternalId: initiateResponse.sessionExternalId,
          answer: {
            type: answer.type as string,
            sdp: answer.sdp as string,
          },
        };

        const answerResponse:
          | OutboundCallAnswerResponse
          | KokattoErrorResponse = await RefreshableRequestHelper.requestKokatto<
          OutboundCallAnswerResponse | KokattoErrorResponse
        >(() =>
          this.inboundCallServiceClient.sendOutboundCallAnswer(
            answerRequest,
            "answer"
          )
        );

        if (isKokattoErrorResponse(answerResponse)) {
          console.log(
            "answerResponse isKokattoErrorResponse",
            JSON.stringify(answerResponse, null, 2)
          );

          throw new Error(`Failed to send answer: ${answerResponse.message}`);
        }

        // Update session status
        this.session.status = CallSessionStatus.RINGING;
        this.setState(OutboundCallState.RINGING);
      }
    } catch (error) {
      this.setState(OutboundCallState.FAILED);
      console.error("Failed to initiate outbound call:", error);
      if (this.events.onError) {
        this.events.onError(error as Error);
      }
      throw error;
    }
  }

  private async createCompleteAnswer(): Promise<RTCSessionDescriptionInit> {
    // Create initial answer
    const answer = await this.webrtc.createAnswer();

    // Wait for ICE gathering to complete (like WhatsApp does)
    await new Promise<void>((resolve) => {
      const peerConnection = this.webrtc.getPeerConnection();

      if (peerConnection.iceGatheringState === "complete") {
        resolve();
      } else {
        const handleIceGatheringStateChange = () => {
          if (peerConnection.iceGatheringState === "complete") {
            peerConnection.removeEventListener(
              "icegatheringstatechange",
              handleIceGatheringStateChange
            );
            console.log("🧊 ICE gathering completed!");
            resolve();
          }
        };

        peerConnection.addEventListener(
          "icegatheringstatechange",
          handleIceGatheringStateChange
        );

        // Fallback timeout
        setTimeout(() => {
          console.log("🧊 ICE gathering timeout - proceeding anyway");
          peerConnection.removeEventListener(
            "icegatheringstatechange",
            handleIceGatheringStateChange
          );
          resolve();
        }, 5000); // 5 second timeout
      }
    });

    // Get the final local description with all ICE candidates
    const finalAnswer = this.webrtc.getPeerConnection().localDescription;
    if (finalAnswer) {
      return finalAnswer;
    }

    return answer;
  }

  async endCall(): Promise<CallRoomModel | { err: string }> {
    try {
      console.log("endCall executed");
      this.setState(OutboundCallState.ENDING);
      const payload: Partial<OutboundCallAnswerRequest> = {
        roomId: this.session.roomId,
      };

      const terminateResponse =
        await RefreshableRequestHelper.requestKokatto<ProcessingIncomingCallResponse>(
          () => {
            return this.inboundCallServiceClient.sendOutboundCallAnswer(
              payload,
              "terminate"
            );
          }
        );
      if (isKokattoErrorResponse(terminateResponse)) {
        throw new Error("Error terminating call");
      }

      return this.room;
    } catch (error) {
      console.error("Error disconnecting call", error);
      throw new Error("Error disconnecting call " + error);
    } finally {
      if (this.remoteAudioElement) {
        this.remoteAudioElement.pause();
        this.remoteAudioElement.srcObject = null;
        this.remoteAudioElement.remove();
        this.remoteAudioElement = null;
      }
      this.webrtc.close();
      this.setState(OutboundCallState.ENDED);
    }
  }

  // Call control methods
  mute(): void {
    this.webrtc.mute();
  }

  unmute(): void {
    this.webrtc.unmute();
  }

  isMuted(): boolean {
    return this.webrtc.isMuted();
  }

  toggleMute(): boolean {
    if (this.isMuted()) {
      this.unmute();
      return false;
    } else {
      this.mute();
      return true;
    }
  }

  // Hold control methods - following inbound call toggle pattern
  async toggleHold(): Promise<boolean> {
    const previousState = this.isOnHold;

    try {
      const holdRequest: Partial<OutboundCallAnswerRequest> = {
        roomId: this.room.roomId,
        isHold: !previousState,
      };

      const holdResponse = await RefreshableRequestHelper.requestKokatto(() =>
        this.inboundCallServiceClient.sendOutboundCallAnswer(
          holdRequest,
          "hold"
        )
      );

      if (isKokattoErrorResponse(holdResponse)) {
        throw new Error(
          `Hold operation failed: ${
            holdResponse.message || holdResponse.reason
          }`
        );
      }

      // Toggle the hold state
      this.isOnHold = !previousState;

      // Handle audio muting based on new state
      if (this.isOnHold) {
        // Going to hold - mute audio
        this.webrtc.mute();
      } else {
        // Coming off hold - unmute audio
        this.webrtc.unmute();
      }

      return this.isOnHold;
    } catch (error) {
      console.error("Failed to toggle hold for outbound call:", error);
      throw error;
    }
  }

  isHeld(): boolean {
    return this.isOnHold;
  }

  // Getters
  getState(): OutboundCallState {
    return this.currentState;
  }

  getCallId(): string | null {
    return this.callId;
  }

  getLocalStream(): MediaStream | null {
    return this.webrtc.getLocalStream();
  }

  getRemoteStream(): MediaStream | null {
    return this.webrtc.getRemoteStream();
  }

  getCallDuration(): number {
    if (!this.startTime) return 0;
    const endTime = this.endTime || Date.now();
    return Math.floor((endTime - this.startTime) / 1000);
  }

  getRoom(): CallRoomModel {
    return this.room;
  }

  getSession(): CallSessionModel {
    return this.session;
  }

  isCallActive(): boolean {
    return [
      OutboundCallState.INITIALIZING,
      OutboundCallState.CALLING,
      OutboundCallState.RINGING,
      OutboundCallState.CONNECTED,
    ].includes(this.currentState);
  }

  // Static factory method
  static create(
    room: CallRoomModel,
    session: CallSessionModel,
    events?: OutboundCallEvents
  ): OutboundCallController {
    return new OutboundCallController(room, session, events);
  }
}
